// src/pages/auth/LoginPage/components/Loginform/LoginForm.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { strapiAuthService } from '../../../../../services/api/strapiAuth';
import SvgIcons from '../../../../../services/SvgIcons';
import './LoginForm.scss';
import { validateField } from '../../../../../services/inputCheck';
import { useAuth } from '../../../../../contexts/AuthContext';

const LoginForm = ({ onForgotPassword }) => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({
        email: '',
        password: '',
        general: ''
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Validation en temps réel
        const error = validateField(name, value);
        setErrors(prev => ({
            ...prev,
            [name]: error,
            general: '' // Réinitialiser l'erreur générale
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({
            email: '',
            password: '',
            general: ''
        });
    
        // Validation des champs
        const emailError = validateField('email', formData.email);
        const passwordError = validateField('password', formData.password);
    
        if (emailError || passwordError) {
            setErrors({
                email: emailError,
                password: passwordError,
                general: ''
            });
            setLoading(false);
            return;
        }
    
        try {
            await login(formData.email, formData.password);  // 👈 Utiliser login du context

            const redirectPath = localStorage.getItem('redirectAfterLogin');
            if (redirectPath) {
                localStorage.removeItem('redirectAfterLogin');
                navigate(redirectPath);
            } else {
                navigate('/mon-compte');
            }

        } catch (err) {
            // Gestion des erreurs Strapi
            const errorMessage = err.message.toLowerCase();

            if (errorMessage.includes('invalid identifier or password') ||
                errorMessage.includes('identifier or password invalid') ||
                errorMessage.includes('identifiants invalides')) {
                setErrors(prev => ({
                    ...prev,
                    general: 'Email ou mot de passe incorrect'
                }));
            } else if (errorMessage.includes('your account email is not confirmed')) {
                setErrors(prev => ({
                    ...prev,
                    general: 'Veuillez confirmer votre adresse email avant de vous connecter'
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    general: err.message || 'Une erreur est survenue lors de la connexion'
                }));
            }
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className="login-form__container">
            <h1>Connexion</h1>
            <form className="login-form" onSubmit={handleSubmit}>
                <div className="login-form__input-group">
                    <label htmlFor="email">Email</label>
                    <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={errors.email ? 'error' : ''}
                        required
                        placeholder="Ton email"
                    />
                    {errors.email && (
                        <span className="login-form__error-message">
                            {errors.email}
                        </span>
                    )}
                </div>

                <div className="login-form__input-group">
                    <label htmlFor="password">Mot de passe</label>
                    <div className="login-form__password-input">
                        <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className={errors.password ? 'error' : ''}
                            required
                            placeholder="Ton mot de passe"
                        />
                        <button
                            type="button"
                            tabIndex={-1}
                            onClick={togglePasswordVisibility}
                            className="login-form__toggle-password"
                            aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        >
                            <SvgIcons
                                name={showPassword ? 'eye' : 'eye-off'}
                                width="24"
                                height="24"
                                color="#36152F"
                            />
                        </button>
                    </div>
                    {errors.password && (
                        <span className="login-form__error-message">
                            {errors.password}
                        </span>
                    )}
                </div>

                <div className="login-form__footer">
                    <button 
                        type="button" 
                        onClick={onForgotPassword}
                        className="login-form__forgot-password"
                    >
                        Mot de passe oublié ?
                    </button>
                </div>

                {errors.general && (
                    <div className="login-form__error">
                        {errors.general}
                    </div>
                )}

                <button
                    type="submit"
                    className="login-form__submit"
                    disabled={loading || Object.values(errors).some(error => error)}
                >
                    {loading ? 'Connexion en cours...' : 'Se connecter'}
                </button>
            </form>
        </div>
    );
};

export default LoginForm;